/**
 * Utility functions for handling guest-to-authenticated user transitions
 */

import { useSessionStore } from '../store';

/**
 * Handle the transition from guest user to authenticated user
 * This permanently migrates guest rooms to the authenticated user's account
 */
export const handleGuestToUserTransition = async (clerkUser) => {
  try {
    console.log('Handling guest-to-user transition for:', clerkUser.id);

    // Get the current session ID that was used for guest rooms
    const sessionStore = useSessionStore.getState();
    const guestSessionId = sessionStore.currentSessionId;

    if (!guestSessionId || guestSessionId === 'guest') {
      console.log('No guest session to transition from');
      return { success: true, message: 'No guest rooms to transition', migratedCount: 0 };
    }

    console.log('✅ User authenticated with preserved session:', guestSessionId);
    console.log('Calling backend to permanently migrate guest rooms...');

    // Call backend to migrate guest rooms to authenticated user
    const { roomApi } = require('../services/api');
    const userInfo = { isSignedIn: true, user: clerkUser };

    try {
      const migrationResult = await roomApi.migrateGuestRooms(userInfo);
      console.log('✅ Room migration completed:', migrationResult);

      return {
        success: true,
        message: migrationResult.message || 'Rooms migrated successfully',
        migratedCount: migrationResult.migratedCount || 0,
        guestSessionId,
        userId: clerkUser.id
      };
    } catch (migrationError) {
      console.error('Room migration failed:', migrationError);
      // Don't fail the whole login, just log the error
      return {
        success: true, // Still return success for login
        message: 'Login successful but room migration failed',
        migratedCount: 0,
        error: migrationError.message
      };
    }
  } catch (error) {
    console.error('Failed to handle guest-to-user transition:', error);
    return {
      success: false,
      error: error.message,
      message: 'Failed to transition guest rooms',
      migratedCount: 0
    };
  }
};


/**
 * Get all rooms for a user, including both their authenticated rooms and any guest rooms from their session
 * The backend automatically includes rooms that match either the user ID or session ID.
 */
export const getAllUserRooms = async (userInfo) => {
  try {
    const { roomApi } = require('../services/api');
    const rooms = await roomApi.getUserRooms(userInfo);

    console.log('DEBUG: getAllUserRooms received from API:', {
      isObject: typeof rooms === 'object',
      hasData: !!rooms?.data,
      dataIsArray: Array.isArray(rooms?.data),
      hasRooms: !!rooms?.data?.rooms,
      roomsIsArray: Array.isArray(rooms?.data?.rooms),
      totalRooms: rooms?.data?.rooms?.length || 0,
      allKeys: Object.keys(rooms || {}),
      dataKeys: Object.keys(rooms?.data || {}),
      firstRoom: rooms?.data?.rooms?.[0],
      fullResponse: rooms
    });

    // The backend already handles this case by looking at both user ID and session ID
    // through the x-session-id header we're sending
    console.log('User rooms loaded (includes both authenticated and guest session rooms)');

    return rooms;
  } catch (error) {
    console.error('Failed to get all user rooms:', error);
    throw error;
  }
};
